import type {
  OutputGroup,
  PageResult,
  SplitSummary,
} from './types';
import type { OcrEngine, OcrImage, PageFields } from './ocr';
import { buildPageResults, groupAndClassify } from './group';
import { buildOutputPdfs } from './buildPdf';
import { buildCsvReport, buildJsonReport, buildPageMapping } from './report';

export interface RenderPageFn {
  (pageNumber: number): Promise<{ image: OcrImage; width: number; height: number }>;
}

export type PipelineStage = 'parse' | 'recognize' | 'group' | 'generate' | 'done' | 'error';

export interface PipelineCallbacks {
  onStage?: (stage: PipelineStage, detail?: string) => void;
  onProgress?: (current: number, total: number) => void;
}

export interface PipelineInput {
  sourcePdfBytes: Uint8Array;
  sourceFileName: string;
  pageCount: number;
  renderPage: RenderPageFn;
  ocr: OcrEngine;
  callbacks?: PipelineCallbacks;
  /** Abort support: throw inside the render callback when cancelled. */
  isCancelled?: () => boolean;
}

export interface PipelineOutput {
  pageResults: PageResult[];
  outputs: OutputGroup[];
  unresolvedPages: number[];
  files: Map<string, Uint8Array>;
  unresolvedPdf: Uint8Array | null;
  summary: SplitSummary;
  jsonReport: string;
  csvReport: string;
  pageMapping: Map<number, string>;
}

/**
 * Run the whole split pipeline: render each page, OCR the fixed label
 * regions, group by tracking number and build the output PDFs.
 * Pages are processed one at a time so images are never all kept in memory.
 */
export async function runSplitPipeline(input: PipelineInput): Promise<PipelineOutput> {
  const { sourcePdfBytes, sourceFileName, pageCount, renderPage, ocr, callbacks, isCancelled } =
    input;
  const startedAt = Date.now();
  callbacks?.onStage?.('parse', `源文件共 ${pageCount} 页`);

  const fields: Array<PageFields & { pageNumber: number }> = [];

  callbacks?.onStage?.('recognize');
  for (let page = 1; page <= pageCount; page += 1) {
    if (isCancelled?.()) throw new Error('任务已取消');
    const { image, width, height } = await renderPage(page);
    const pageFields = await ocr.recognizePageFields(image, width, height);
    fields.push({ pageNumber: page, ...pageFields });
    callbacks?.onProgress?.(page, pageCount);
    if (page % 10 === 0 || page === pageCount) {
      callbacks?.onStage?.('recognize', `正在处理第 ${page} / ${pageCount} 页`);
    }
  }

  callbacks?.onStage?.('group');
  const pageResults = buildPageResults(fields);
  const grouping = groupAndClassify(pageResults, sourceFileName, startedAt);
  const { outputs, unresolvedPages, summary } = grouping;

  callbacks?.onStage?.('generate', `正在生成 ${outputs.length} 个 PDF`);
  const built = await buildOutputPdfs(sourcePdfBytes, outputs, unresolvedPages);
  if (built.unresolvedPdf) {
    built.files.set('unresolved-pages.pdf', built.unresolvedPdf);
  }

  const pageMapping = buildPageMapping(pageResults, outputs);
  const jsonReport = buildJsonReport(sourceFileName, summary, pageResults);
  const csvReport = buildCsvReport(sourceFileName, pageResults, pageMapping);

  summary.elapsedMs = Date.now() - startedAt;
  callbacks?.onStage?.('done', `完成，共生成 ${summary.outputFileCount} 个文件`);

  return {
    pageResults,
    outputs,
    unresolvedPages,
    files: built.files,
    unresolvedPdf: built.unresolvedPdf,
    summary,
    jsonReport,
    csvReport,
    pageMapping,
  };
}
